<template>
  <div style="max-width: 90%; margin: auto" class="py-10">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-8 flex-wrap" style="gap: 16px">
      <div>
        <h1 class="page-title">Section Pipeline</h1>
        <p class="page-subtitle">Verwalten Sie Ihre Section-Ideen und Entwicklungen</p>
      </div>
      <button v-if="$can('pipeline.create')" class="primary-btn" @click="openCreateDialog">
        <v-icon size="16">mdi-plus</v-icon>
        <span>Neue Idee</span>
      </button>
    </div>

    <!-- Tabs -->
    <div class="tab-bar mb-4">
      <button
        class="tab"
        :class="{ 'tab-active': activeTab === 'ideas' }"
        @click="activeTab = 'ideas'"
      >
        <v-icon size="16">mdi-lightbulb-outline</v-icon>
        Ideen
        <span class="tab-count">{{ ideas.length }}</span>
      </button>
      <button
        class="tab"
        :class="{ 'tab-active': activeTab === 'development' }"
        @click="activeTab = 'development'"
      >
        <v-icon size="16">mdi-code-braces</v-icon>
        Entwicklung
        <span class="tab-count">{{ developments.length }}</span>
      </button>

      <div style="flex: 1"></div>

      <div class="view-toggle">
        <button class="view-btn" :class="{ active: viewMode === 'grid' }" title="Grid" @click="setViewMode('grid')">
          <v-icon size="16">mdi-view-grid-outline</v-icon>
        </button>
        <button class="view-btn" :class="{ active: viewMode === 'list' }" title="Liste" @click="setViewMode('list')">
          <v-icon size="16">mdi-format-list-bulleted</v-icon>
        </button>
      </div>
    </div>

    <!-- IDEAS -->
    <template v-if="activeTab === 'ideas'">
      <div v-if="loadingIdeas" class="loading-state">
        <div v-for="i in 6" :key="`skel-i-${i}`" class="skel-row"></div>
      </div>

      <div v-else-if="ideas.length === 0" class="empty-state">
        <v-icon size="48" color="#c5cbd3" class="mb-4">mdi-lightbulb-outline</v-icon>
        <h3 class="empty-title">Keine Ideen vorhanden</h3>
        <p class="empty-desc">Erstellen Sie Ihre erste Section-Idee.</p>
        <button v-if="$can('pipeline.create')" class="primary-btn mt-4" @click="openCreateDialog">
          <v-icon size="16">mdi-plus</v-icon>
          Neue Idee erstellen
        </button>
      </div>

      <!-- Grid View -->
      <div v-else-if="viewMode === 'grid'" class="grid-items">
        <div v-for="idea in ideas" :key="idea.id" class="item-card">
          <div class="item-thumb" @click.stop="openImageViewer(idea, 'idea')">
            <v-img
              v-if="idea.images && idea.images.length > 0"
              :src="`/api/pipeline/ideas/image/${idea.id}/0`"
              cover
              aspect-ratio="1"
            >
              <template #error>
                <div class="thumb-fallback"><v-icon size="28" color="#c5cbd3">mdi-image-off-outline</v-icon></div>
              </template>
            </v-img>
            <div v-else class="thumb-fallback" style="aspect-ratio: 1">
              <v-icon size="28" color="#c5cbd3">mdi-image-off-outline</v-icon>
            </div>
            <span v-if="idea.images && idea.images.length > 0" class="thumb-count">
              <v-icon size="11">mdi-image-multiple-outline</v-icon>{{ idea.images.length }}
            </span>
          </div>

          <div class="pa-4">
            <span class="handle-chip chip-ideas">{{ idea.handle }}</span>
            <div class="item-name mt-2">{{ idea.name }}</div>
            <div v-if="idea.seenAt" class="item-sub"><v-icon size="11">mdi-eye-outline</v-icon>{{ idea.seenAt }}</div>
            <div class="item-meta mt-3">
              <div v-if="idea.assignedEmployee" class="meta-assigned">
                <v-avatar size="20">
                  <v-img :src="`/api/employees/${idea.assignedEmployee.id}/image`" cover>
                    <template #error><v-icon size="12">mdi-account</v-icon></template>
                  </v-img>
                </v-avatar>
                <span>{{ idea.assignedEmployee.firstname }} {{ idea.assignedEmployee.lastname }}</span>
              </div>
              <div v-else class="meta-assigned meta-assigned-empty">Nicht zugewiesen</div>
              <div class="meta-date">{{ formatDate(idea.createdAt) }}</div>
            </div>

            <div class="item-actions">
              <button class="action-btn action-primary" title="Zu Entwicklung" @click.stop="moveToDevDialog(idea)">
                <v-icon size="16">mdi-arrow-right</v-icon>
              </button>
              <button class="action-btn" title="Bearbeiten" @click.stop="openEditDialog(idea, 'idea')">
                <v-icon size="16">mdi-pencil-outline</v-icon>
              </button>
              <button class="action-btn action-danger" title="Löschen" @click.stop="deleteItem(idea.id, 'idea')">
                <v-icon size="16">mdi-delete-outline</v-icon>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- List View -->
      <div v-else class="list-wrap">
        <div class="list-header list-cols-ideas">
          <div></div>
          <div>Handle</div>
          <div>Name</div>
          <div>Wo gesehen</div>
          <div>Zugewiesen</div>
          <div>Bilder</div>
          <div>Erstellt</div>
          <div style="text-align: right">Aktionen</div>
        </div>
        <div v-for="idea in ideas" :key="idea.id" class="list-row list-cols-ideas">
          <div>
            <div class="list-thumb" @click.stop="openImageViewer(idea, 'idea')">
              <v-img v-if="idea.images && idea.images.length > 0" :src="`/api/pipeline/ideas/image/${idea.id}/0`" cover width="40" height="40">
                <template #error><div class="thumb-fallback"><v-icon size="16" color="#c5cbd3">mdi-image-off-outline</v-icon></div></template>
              </v-img>
              <div v-else class="thumb-fallback"><v-icon size="16" color="#c5cbd3">mdi-image-off-outline</v-icon></div>
            </div>
          </div>
          <div><span class="handle-chip chip-ideas">{{ idea.handle }}</span></div>
          <div class="list-name">{{ idea.name }}</div>
          <div class="list-muted">{{ idea.seenAt || '—' }}</div>
          <div>
            <div v-if="idea.assignedEmployee" class="meta-assigned">
              <v-avatar size="20">
                <v-img :src="`/api/employees/${idea.assignedEmployee.id}/image`" cover>
                  <template #error><v-icon size="12">mdi-account</v-icon></template>
                </v-img>
              </v-avatar>
              <span>{{ idea.assignedEmployee.firstname }} {{ idea.assignedEmployee.lastname }}</span>
            </div>
            <span v-else class="list-muted">—</span>
          </div>
          <div>
            <span v-if="idea.images && idea.images.length > 0" class="pill pill-neutral">{{ idea.images.length }}</span>
            <span v-else class="list-muted">—</span>
          </div>
          <div class="list-muted">{{ formatDate(idea.createdAt) }}</div>
          <div class="list-actions">
            <button class="action-btn action-primary" title="Zu Entwicklung" @click.stop="moveToDevDialog(idea)"><v-icon size="15">mdi-arrow-right</v-icon></button>
            <button class="action-btn" title="Bearbeiten" @click.stop="openEditDialog(idea, 'idea')"><v-icon size="15">mdi-pencil-outline</v-icon></button>
            <button class="action-btn action-danger" title="Löschen" @click.stop="deleteItem(idea.id, 'idea')"><v-icon size="15">mdi-delete-outline</v-icon></button>
          </div>
        </div>
      </div>
    </template>

    <!-- DEVELOPMENT -->
    <template v-else>
      <div v-if="loadingDevelopments" class="loading-state">
        <div v-for="i in 6" :key="`skel-d-${i}`" class="skel-row"></div>
      </div>

      <div v-else-if="developments.length === 0" class="empty-state">
        <v-icon size="48" color="#c5cbd3" class="mb-4">mdi-code-braces</v-icon>
        <h3 class="empty-title">Keine Entwicklungen vorhanden</h3>
        <p class="empty-desc">Verschieben Sie Ideen in die Entwicklung.</p>
      </div>

      <!-- Grid View -->
      <div v-else-if="viewMode === 'grid'" class="grid-items">
        <div v-for="dev in developments" :key="dev.id" class="item-card">
          <div class="item-thumb" @click.stop="openImageViewer(dev, 'development')">
            <v-img
              v-if="dev.sectionImage"
              :src="`/api/pipeline/development/section-image/${dev.id}`"
              cover
              aspect-ratio="1"
            />
            <v-img
              v-else-if="dev.images && dev.images.length > 0"
              :src="`/api/pipeline/development/image/${dev.id}/0`"
              cover
              aspect-ratio="1"
            />
            <div v-else class="thumb-fallback" style="aspect-ratio: 1">
              <v-icon size="28" color="#c5cbd3">mdi-image-off-outline</v-icon>
            </div>
            <span v-if="dev.sectionImage" class="thumb-count thumb-success">
              <v-icon size="11">mdi-check-circle-outline</v-icon>Section Image
            </span>
          </div>

          <div class="pa-4">
            <span class="handle-chip chip-dev">{{ dev.handle }}</span>
            <div class="item-name mt-2">{{ dev.name }}</div>
            <div v-if="dev.seenAt" class="item-sub"><v-icon size="11">mdi-eye-outline</v-icon>{{ dev.seenAt }}</div>

            <div class="mt-3">
              <span v-if="dev.sectionImage" class="pill pill-success">
                <v-icon size="11">mdi-check-circle-outline</v-icon>Section Image ok
              </span>
              <span v-else class="pill pill-warning">
                <v-icon size="11">mdi-alert-circle-outline</v-icon>Bild fehlt
              </span>
            </div>

            <div class="item-meta mt-3">
              <div v-if="dev.assignedEmployee" class="meta-assigned">
                <v-avatar size="20">
                  <v-img :src="`/api/employees/${dev.assignedEmployee.id}/image`" cover>
                    <template #error><v-icon size="12">mdi-account</v-icon></template>
                  </v-img>
                </v-avatar>
                <span>{{ dev.assignedEmployee.firstname }} {{ dev.assignedEmployee.lastname }}</span>
              </div>
              <div v-else class="meta-assigned meta-assigned-empty">Nicht zugewiesen</div>
              <div class="meta-date">{{ formatDate(dev.createdAt) }}</div>
            </div>

            <div class="item-actions">
              <button class="action-btn" title="Section Image" @click.stop="openSectionImageDialog(dev)">
                <v-icon size="16">mdi-image-plus</v-icon>
              </button>
              <button
                class="action-btn action-success"
                :class="{ 'action-disabled': !dev.sectionImage }"
                title="Section erstellen"
                :disabled="!dev.sectionImage"
                @click.stop="openCreateSectionDialog(dev)"
              >
                <v-icon size="16">mdi-check-circle-outline</v-icon>
              </button>
              <button class="action-btn" title="Bearbeiten" @click.stop="openEditDialog(dev, 'development')">
                <v-icon size="16">mdi-pencil-outline</v-icon>
              </button>
              <button class="action-btn action-danger" title="Löschen" @click.stop="deleteItem(dev.id, 'development')">
                <v-icon size="16">mdi-delete-outline</v-icon>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- List View -->
      <div v-else class="list-wrap">
        <div class="list-header list-cols-dev">
          <div></div>
          <div>Handle</div>
          <div>Name</div>
          <div>Zugewiesen</div>
          <div>Status</div>
          <div>Erstellt</div>
          <div style="text-align: right">Aktionen</div>
        </div>
        <div v-for="dev in developments" :key="dev.id" class="list-row list-cols-dev">
          <div>
            <div class="list-thumb" @click.stop="openImageViewer(dev, 'development')">
              <v-img v-if="dev.sectionImage" :src="`/api/pipeline/development/section-image/${dev.id}`" cover width="40" height="40" />
              <v-img v-else-if="dev.images && dev.images.length > 0" :src="`/api/pipeline/development/image/${dev.id}/0`" cover width="40" height="40" />
              <div v-else class="thumb-fallback"><v-icon size="16" color="#c5cbd3">mdi-image-off-outline</v-icon></div>
            </div>
          </div>
          <div><span class="handle-chip chip-dev">{{ dev.handle }}</span></div>
          <div class="list-name">{{ dev.name }}</div>
          <div>
            <div v-if="dev.assignedEmployee" class="meta-assigned">
              <v-avatar size="20">
                <v-img :src="`/api/employees/${dev.assignedEmployee.id}/image`" cover>
                  <template #error><v-icon size="12">mdi-account</v-icon></template>
                </v-img>
              </v-avatar>
              <span>{{ dev.assignedEmployee.firstname }} {{ dev.assignedEmployee.lastname }}</span>
            </div>
            <span v-else class="list-muted">—</span>
          </div>
          <div>
            <span v-if="dev.sectionImage" class="pill pill-success"><v-icon size="11">mdi-check-circle-outline</v-icon>Bild ok</span>
            <span v-else class="pill pill-warning"><v-icon size="11">mdi-alert-circle-outline</v-icon>Bild fehlt</span>
          </div>
          <div class="list-muted">{{ formatDate(dev.createdAt) }}</div>
          <div class="list-actions">
            <button class="action-btn" title="Section Image" @click.stop="openSectionImageDialog(dev)"><v-icon size="15">mdi-image-plus</v-icon></button>
            <button
              class="action-btn action-success"
              :class="{ 'action-disabled': !dev.sectionImage }"
              title="Section erstellen"
              :disabled="!dev.sectionImage"
              @click.stop="openCreateSectionDialog(dev)"
            ><v-icon size="15">mdi-check-circle-outline</v-icon></button>
            <button class="action-btn" title="Bearbeiten" @click.stop="openEditDialog(dev, 'development')"><v-icon size="15">mdi-pencil-outline</v-icon></button>
            <button class="action-btn action-danger" title="Löschen" @click.stop="deleteItem(dev.id, 'development')"><v-icon size="15">mdi-delete-outline</v-icon></button>
          </div>
        </div>
      </div>
    </template>

    <!-- ========== DIALOGS ========== -->

    <!-- Image Viewer -->
    <div v-if="imageViewerDialog" class="dialog-backdrop" @click="imageViewerDialog = false"></div>
    <v-dialog v-model="imageViewerDialog" max-width="1000" :scrim="false">
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" @click="imageViewerDialog = false"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon"><v-icon size="22" color="#0a1628">mdi-image-multiple-outline</v-icon></div>
          <div>
            <div class="dh-title">Bilder Vorschau</div>
            <div class="dh-sub">{{ viewerImages.length }} {{ viewerImages.length === 1 ? 'Bild' : 'Bilder' }}</div>
          </div>
        </div>
        <div class="db">
          <div v-if="viewerImages.length > 0" class="viewer-grid">
            <div v-for="(image, index) in viewerImages" :key="index" class="viewer-item">
              <v-img :src="image" cover aspect-ratio="1" />
            </div>
          </div>
          <div v-else class="empty-inline">Keine Bilder vorhanden</div>
        </div>
      </v-card>
    </v-dialog>

    <!-- Main Create/Edit Dialog -->
    <div v-if="dialog" class="dialog-backdrop" @click="!saving && closeDialog()"></div>
    <v-dialog v-model="dialog" max-width="640" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" :disabled="saving" @click="closeDialog"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon"><v-icon size="22" color="#0a1628">{{ editMode ? 'mdi-pencil-outline' : 'mdi-lightbulb-outline' }}</v-icon></div>
          <div>
            <div class="dh-title">{{ editMode ? 'Bearbeiten' : 'Neue Idee erstellen' }}</div>
            <div class="dh-sub">{{ editMode ? 'Daten anpassen und speichern' : 'Halte eine Section-Inspiration fest' }}</div>
          </div>
        </div>
        <div class="db">
          <div class="fg mb-3">
            <label class="fl">Name *</label>
            <div class="iw" :class="{ 'iw-f': focusField === 'name' }">
              <v-icon size="15" color="rgba(0,0,0,0.25)">mdi-format-title</v-icon>
              <input v-model="formData.name" class="ii" placeholder="z.B. Hero Section mit Video" :disabled="saving" @focus="focusField = 'name'" @blur="focusField = null" />
            </div>
          </div>

          <div class="fg mb-3">
            <label class="fl">Handle <span class="fl-hint">(automatisch generiert)</span></label>
            <div class="iw iw-readonly">
              <v-icon size="15" color="rgba(0,0,0,0.25)">mdi-link-variant</v-icon>
              <input v-model="formData.handle" class="ii" readonly />
            </div>
          </div>

          <div class="fg mb-3">
            <label class="fl">Wo gesehen?</label>
            <div class="iw" :class="{ 'iw-f': focusField === 'seenAt' }">
              <v-icon size="15" color="rgba(0,0,0,0.25)">mdi-eye-outline</v-icon>
              <input v-model="formData.seenAt" class="ii" placeholder="z.B. Nike Store, Apple Website" :disabled="saving" @focus="focusField = 'seenAt'" @blur="focusField = null" />
            </div>
          </div>

          <div class="fg mb-3" ref="employeeDropdown">
            <label class="fl">Mitarbeiter zuweisen</label>
            <div class="emp-wrap">
              <button
                type="button"
                class="emp-trigger"
                :class="{ 'emp-trigger-open': employeeMenuOpen }"
                :disabled="saving"
                @click="employeeMenuOpen = !employeeMenuOpen"
              >
                <template v-if="selectedEmployee">
                  <v-avatar size="22" class="emp-avatar">
                    <v-img :src="`/api/employees/${selectedEmployee.id}/image`" cover>
                      <template #error><v-icon size="14" color="#888">mdi-account</v-icon></template>
                    </v-img>
                  </v-avatar>
                  <span class="emp-name">{{ selectedEmployee.fullName }}</span>
                </template>
                <template v-else>
                  <div class="emp-avatar emp-avatar-empty"><v-icon size="14" color="#bbb">mdi-account-outline</v-icon></div>
                  <span class="emp-placeholder">Niemandem zugewiesen</span>
                </template>
                <v-icon size="16" color="#888" class="emp-chev" :class="{ 'emp-chev-open': employeeMenuOpen }">mdi-chevron-down</v-icon>
              </button>

              <div v-if="employeeMenuOpen" class="emp-menu">
                <button type="button" class="emp-option" :class="{ 'emp-option-active': formData.assignedTo === null }" @click="selectEmployee(null)">
                  <div class="emp-avatar emp-avatar-empty"><v-icon size="14" color="#bbb">mdi-account-outline</v-icon></div>
                  <span class="emp-placeholder">Niemandem zugewiesen</span>
                  <v-icon v-if="formData.assignedTo === null" size="16" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
                </button>
                <div class="emp-divider"></div>
                <button
                  v-for="emp in employees"
                  :key="emp.id"
                  type="button"
                  class="emp-option"
                  :class="{ 'emp-option-active': formData.assignedTo === emp.id }"
                  @click="selectEmployee(emp.id)"
                >
                  <v-avatar size="24" class="emp-avatar">
                    <v-img :src="`/api/employees/${emp.id}/image`" cover>
                      <template #error><v-icon size="14" color="#888">mdi-account</v-icon></template>
                    </v-img>
                  </v-avatar>
                  <div class="emp-option-body">
                    <div class="emp-name">{{ emp.fullName }}</div>
                    <div v-if="emp.position" class="emp-position">{{ emp.position }}</div>
                  </div>
                  <v-icon v-if="formData.assignedTo === emp.id" size="16" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
                </button>
              </div>
            </div>
            <div v-if="employeeMenuOpen" class="emp-spacer"></div>
          </div>

          <div class="fg">
            <label class="fl">Bilder</label>
            <input ref="imagesInput" type="file" accept="image/*" multiple style="display: none" @change="onImagesSelected" />

            <div v-if="imagePreviews.length > 0" class="images-preview-grid mb-3">
              <div v-for="(preview, index) in imagePreviews" :key="index" class="preview-thumb">
                <v-img :src="preview" cover aspect-ratio="1" />
                <button class="preview-remove" @click="removeImage(index)" :disabled="saving">
                  <v-icon size="12">mdi-close</v-icon>
                </button>
              </div>
            </div>

            <button class="bs" :disabled="saving" @click="$refs.imagesInput.click()">
              <v-icon size="15">mdi-image-plus</v-icon>
              Bilder hinzufügen
            </button>
          </div>

          <div v-if="dialogError" class="alert-error mt-4">
            <v-icon size="16" color="#D32F2F">mdi-alert-circle-outline</v-icon>{{ dialogError }}
          </div>
        </div>
        <div class="dff">
          <button class="bo" :disabled="saving" @click="closeDialog">Abbrechen</button>
          <button class="bp" :disabled="!formData.name || saving" @click="saveItem">
            <v-progress-circular v-if="saving" indeterminate size="14" width="2" color="white" />
            <span v-else>{{ editMode ? 'Speichern' : 'Erstellen' }}</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Move to Dev Dialog -->
    <div v-if="moveDialog" class="dialog-backdrop" @click="!moving && (moveDialog = false)"></div>
    <v-dialog v-model="moveDialog" max-width="520" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" :disabled="moving" @click="moveDialog = false"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon" style="background: rgba(255,152,0,0.1)"><v-icon size="22" color="#E65100">mdi-arrow-right</v-icon></div>
          <div>
            <div class="dh-title">Zu Entwicklung verschieben</div>
            <div class="dh-sub">Die Idee wird aus der Ideen-Liste entfernt</div>
          </div>
        </div>
        <div class="db">
          <div class="summary-card">
            <span class="handle-chip chip-ideas">{{ selectedIdea?.handle }}</span>
            <div class="summary-name">{{ selectedIdea?.name }}</div>
            <div v-if="selectedIdea?.seenAt" class="summary-sub">Gesehen: {{ selectedIdea.seenAt }}</div>
          </div>
        </div>
        <div class="dff">
          <button class="bo" :disabled="moving" @click="moveDialog = false">Abbrechen</button>
          <button class="bp bp-warning" :disabled="moving" @click="confirmMoveToDev">
            <v-progress-circular v-if="moving" indeterminate size="14" width="2" color="white" />
            <span v-else>Verschieben</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Section Image Dialog -->
    <div v-if="sectionImageDialog" class="dialog-backdrop" @click="!uploadingSectionImage && closeSectionImageDialog()"></div>
    <v-dialog v-model="sectionImageDialog" max-width="560" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" :disabled="uploadingSectionImage" @click="closeSectionImageDialog"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon"><v-icon size="22" color="#0a1628">mdi-image-outline</v-icon></div>
          <div>
            <div class="dh-title">Section Image festlegen</div>
            <div class="dh-sub">Vorschaubild für die fertige Section</div>
          </div>
        </div>
        <div class="db">
          <div class="mode-toggle mb-4">
            <button
              class="mode-btn"
              :class="{ 'mode-active': sectionImageMode === 'upload' }"
              @click="sectionImageMode = 'upload'"
            >
              <v-icon size="15">mdi-upload</v-icon>Neu hochladen
            </button>
            <button
              class="mode-btn"
              :class="{ 'mode-active': sectionImageMode === 'existing' }"
              :disabled="!selectedDev || !selectedDev.images || selectedDev.images.length === 0"
              @click="sectionImageMode = 'existing'"
            >
              <v-icon size="15">mdi-image-multiple-outline</v-icon>Vorhandenes nutzen
            </button>
          </div>

          <template v-if="sectionImageMode === 'upload'">
            <input ref="sectionImageInput" type="file" accept="image/*" style="display: none" @change="onSectionImageSelected" />
            <div class="upload-box" @click="$refs.sectionImageInput.click()">
              <v-img v-if="sectionImagePreview" :src="sectionImagePreview" cover aspect-ratio="1" />
              <div v-else class="upload-placeholder">
                <v-icon size="40" color="#c5cbd3" class="mb-2">mdi-camera-outline</v-icon>
                <div class="upload-title">Bild hochladen</div>
                <div class="upload-sub">Klicke hier zum Auswählen</div>
              </div>
            </div>
            <button v-if="sectionImagePreview" class="bs mt-3" @click="$refs.sectionImageInput.click()">
              <v-icon size="15">mdi-image-edit-outline</v-icon>Bild ändern
            </button>
          </template>

          <template v-else>
            <div v-if="selectedDevImages.length === 0" class="empty-inline">Keine Vorschau-Bilder vorhanden</div>
            <div v-else class="existing-grid">
              <div
                v-for="(img, index) in selectedDevImages"
                :key="index"
                class="existing-item"
                :class="{ 'existing-selected': selectedExistingImageIndex === index }"
                @click="selectedExistingImageIndex = index"
              >
                <v-img :src="`/api/pipeline/development/image/${selectedDev.id}/${index}`" cover aspect-ratio="1" />
                <v-icon v-if="selectedExistingImageIndex === index" class="existing-check" size="22" color="#2E7D32">mdi-check-circle</v-icon>
              </div>
            </div>
          </template>
        </div>
        <div class="dff">
          <button class="bo" :disabled="uploadingSectionImage" @click="closeSectionImageDialog">Abbrechen</button>
          <button
            v-if="sectionImageMode === 'upload'"
            class="bp"
            :disabled="!sectionImageFile || uploadingSectionImage"
            @click="uploadSectionImage"
          >
            <v-progress-circular v-if="uploadingSectionImage" indeterminate size="14" width="2" color="white" />
            <span v-else>Hochladen</span>
          </button>
          <button
            v-else
            class="bp"
            :disabled="selectedExistingImageIndex === null || uploadingSectionImage"
            @click="useExistingImageAsSectionImage"
          >
            <v-progress-circular v-if="uploadingSectionImage" indeterminate size="14" width="2" color="white" />
            <span v-else>Übernehmen</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Create Section Dialog -->
    <div v-if="createSectionDialog" class="dialog-backdrop" @click="!creatingSection && closeCreateSectionDialog()"></div>
    <v-dialog v-model="createSectionDialog" max-width="520" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" :disabled="creatingSection" @click="closeCreateSectionDialog"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon" style="background: rgba(46,125,50,0.1)"><v-icon size="22" color="#2E7D32">mdi-check-circle-outline</v-icon></div>
          <div>
            <div class="dh-title">Section erstellen</div>
            <div class="dh-sub">Aus Entwicklung eine echte Section machen</div>
          </div>
        </div>
        <div class="db">
          <div class="summary-card d-flex align-center" style="gap: 12px">
            <v-avatar size="44" rounded="lg" style="background: #f8f9fb">
              <v-img v-if="selectedDevForSection?.sectionImage" :src="`/api/pipeline/development/section-image/${selectedDevForSection.id}`" cover />
              <v-icon v-else color="#c5cbd3">mdi-image-outline</v-icon>
            </v-avatar>
            <div style="flex: 1; min-width: 0">
              <span class="handle-chip chip-dev">{{ selectedDevForSection?.handle }}</span>
              <div class="summary-name mt-1">{{ selectedDevForSection?.name }}</div>
              <div v-if="selectedDevForSection?.seenAt" class="summary-sub">Gesehen: {{ selectedDevForSection.seenAt }}</div>
            </div>
          </div>

          <div class="info-banner mt-4">
            <v-icon size="16" color="#5B5BD6">mdi-information-outline</v-icon>
            Die Section wird als Entwurf erstellt und kann danach bearbeitet werden.
          </div>

          <div v-if="createSectionError" class="alert-error mt-3">
            <v-icon size="16" color="#D32F2F">mdi-alert-circle-outline</v-icon>{{ createSectionError }}
          </div>
        </div>
        <div class="dff">
          <button class="bo" :disabled="creatingSection" @click="closeCreateSectionDialog">Abbrechen</button>
          <button class="bp bp-success" :disabled="creatingSection" @click="confirmCreateSection">
            <v-progress-circular v-if="creatingSection" indeterminate size="14" width="2" color="white" />
            <span v-else>Section erstellen</span>
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'Pipeline',

  data() {
    return {
      activeTab: 'ideas',
      viewMode: localStorage.getItem('pipeline.viewMode') === 'list' ? 'list' : 'grid',
      ideas: [],
      developments: [],
      employees: [],
      loadingIdeas: true,
      loadingDevelopments: true,
      dialog: false,
      moveDialog: false,
      sectionImageDialog: false,
      createSectionDialog: false,
      imageViewerDialog: false,
      editMode: false,
      saving: false,
      moving: false,
      uploadingSectionImage: false,
      creatingSection: false,
      dialogError: '',
      createSectionError: '',
      focusField: null,
      employeeMenuOpen: false,
      formData: { id: null, name: '', handle: '', seenAt: '', assignedTo: null },
      imageFiles: [],
      imagePreviews: [],
      existingImages: [],
      removedImageIndexes: [],
      selectedIdea: null,
      selectedDev: null,
      selectedDevForSection: null,
      sectionImageFile: null,
      sectionImagePreview: null,
      sectionImageMode: 'upload',
      selectedExistingImageIndex: null,
      viewerImages: [],
      usedHandles: new Set(),
      baseNumber: null,
      debounceTimer: null,
      debounceMs: 400,
    };
  },

  watch: {
    'formData.name'(val) {
      if (!this.editMode) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          const t = (val || '').trim();
          if (!t) { this.formData.handle = ''; return; }
          this.updateHandleFromTitle(t);
        }, this.debounceMs);
      }
    },
  },

  mounted() {
    this.loadData();
    document.addEventListener('click', this.handleOutsideClick);
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },

  computed: {
    selectedDevImages() {
      if (!this.selectedDev || !this.selectedDev.images) return [];
      return this.selectedDev.images;
    },
    selectedEmployee() {
      if (!this.formData.assignedTo) return null;
      return this.employees.find((e) => e.id === this.formData.assignedTo) || null;
    },
  },

  methods: {
    setViewMode(mode) {
      this.viewMode = mode;
      localStorage.setItem('pipeline.viewMode', mode);
    },

    selectEmployee(id) {
      this.formData.assignedTo = id;
      this.employeeMenuOpen = false;
    },

    handleOutsideClick(e) {
      if (!this.employeeMenuOpen) return;
      const el = this.$refs.employeeDropdown;
      if (el && !el.contains(e.target)) this.employeeMenuOpen = false;
    },

    async loadData() {
      await Promise.all([this.loadIdeas(), this.loadDevelopments(), this.loadEmployees(), this.getAllHandles()]);
    },

    slugifyTitle(t) {
      if (!t) return '';
      const map = { ä: 'ae', ö: 'oe', ü: 'ue', Ä: 'ae', Ö: 'oe', Ü: 'ue', ß: 'ss' };
      return t.replace(/[äöüÄÖÜß]/g, (m) => map[m] || m)
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
        .replace(/[^a-z0-9]+/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    },

    getNextAvailableNumber() {
      let num = (this.baseNumber || 1000) + 1;
      while (this.usedHandles.has(num)) num++;
      return num;
    },

    updateHandleFromTitle(title) {
      const num = this.getNextAvailableNumber();
      const slug = this.slugifyTitle(title);
      this.formData.handle = slug ? `${num}_${slug}` : '';
    },

    async getAllHandles() {
      try {
        const { data } = await axios.get('/api/sections/all-handles');
        const numbers = new Set();
        let maxNumber = 1000;
        data.handles.forEach((handle) => {
          const match = handle.match(/^(\d+)_/);
          if (match) {
            const num = parseInt(match[1], 10);
            numbers.add(num);
            if (num > maxNumber) maxNumber = num;
          }
        });
        this.usedHandles = numbers;
        this.baseNumber = maxNumber;
      } catch (e) {
        console.error('Fehler beim Laden der Handles!', e);
        this.usedHandles = new Set();
        this.baseNumber = 1000;
      }
    },

    async loadEmployees() {
      try {
        const { data } = await axios.get('/api/employees/get');
        this.employees = (Array.isArray(data) ? data : []).map((emp) => ({ ...emp, fullName: `${emp.firstname} ${emp.lastname}` }));
      } catch (e) {
        console.error('Fehler beim Laden der Mitarbeiter!', e);
        this.employees = [];
      }
    },

    async loadIdeas() {
      this.loadingIdeas = true;
      try {
        const { data } = await axios.get('/api/pipeline/ideas/get');
        this.ideas = data.data || [];
      } catch (e) {
        console.error('Fehler beim Laden der Ideen!', e);
        this.ideas = [];
      } finally {
        this.loadingIdeas = false;
      }
    },

    async loadDevelopments() {
      this.loadingDevelopments = true;
      try {
        const { data } = await axios.get('/api/pipeline/development/get');
        this.developments = data.data || [];
      } catch (e) {
        console.error('Fehler beim Laden der Entwicklungen!', e);
        this.developments = [];
      } finally {
        this.loadingDevelopments = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '—';
      return new Intl.DateTimeFormat('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      }).format(new Date(dateString));
    },

    openImageViewer(item, type) {
      this.viewerImages = [];
      if (type === 'development' && item.sectionImage) {
        this.viewerImages.push(`/api/pipeline/development/section-image/${item.id}`);
      }
      if (item.images && item.images.length > 0) {
        const baseUrl = type === 'idea'
          ? `/api/pipeline/ideas/image/${item.id}/`
          : `/api/pipeline/development/image/${item.id}/`;
        item.images.forEach((_, index) => this.viewerImages.push(`${baseUrl}${index}`));
      }
      if (this.viewerImages.length > 0) this.imageViewerDialog = true;
    },

    openCreateDialog() {
      this.editMode = false;
      this.formData = { id: null, name: '', handle: '', seenAt: '', assignedTo: null };
      this.imageFiles = []; this.imagePreviews = []; this.existingImages = []; this.removedImageIndexes = [];
      this.dialogError = '';
      this.dialog = true;
    },

    async openEditDialog(item, type) {
      this.editMode = true;
      this.formData = {
        id: item.id,
        name: item.name,
        handle: item.handle,
        seenAt: item.seenAt || '',
        assignedTo: item.assignedTo || null,
      };
      this.imageFiles = []; this.imagePreviews = []; this.removedImageIndexes = [];
      this.dialogError = '';

      if (item.images && item.images.length > 0) {
        this.existingImages = item.images.map((_, index) => index);
        this.imagePreviews = item.images.map((_, index) =>
          type === 'idea'
            ? `/api/pipeline/ideas/image/${item.id}/${index}`
            : `/api/pipeline/development/image/${item.id}/${index}`
        );
      } else {
        this.existingImages = [];
      }
      if (type === 'development') this.selectedDev = item;
      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.editMode = false;
      this.employeeMenuOpen = false;
      this.formData = { id: null, name: '', handle: '', seenAt: '', assignedTo: null };
      this.imageFiles = []; this.imagePreviews = []; this.existingImages = []; this.removedImageIndexes = [];
      this.dialogError = '';
      this.selectedDev = null;
    },

    onImagesSelected(e) {
      const files = Array.from(e.target.files || []);
      files.forEach((file) => {
        this.imageFiles.push(file);
        const reader = new FileReader();
        reader.onload = () => this.imagePreviews.push(reader.result);
        reader.readAsDataURL(file);
      });
    },

    removeImage(index) {
      if (index < this.existingImages.length) {
        const originalIndex = this.existingImages[index];
        this.removedImageIndexes.push(originalIndex);
        this.existingImages.splice(index, 1);
      } else {
        const fileIndex = index - this.existingImages.length;
        this.imageFiles.splice(fileIndex, 1);
      }
      this.imagePreviews.splice(index, 1);
    },

    async saveItem() {
      this.saving = true;
      this.dialogError = '';
      try {
        const form = new FormData();
        form.append('name', this.formData.name);
        form.append('handle', this.formData.handle);
        form.append('seenAt', this.formData.seenAt || '');
        form.append('assignedTo', this.formData.assignedTo || '');
        this.imageFiles.forEach((file) => form.append('images', file));

        if (this.editMode) {
          form.append('id', this.formData.id);
          form.append('removedImages', JSON.stringify(this.removedImageIndexes));
          const endpoint = this.selectedDev ? '/api/pipeline/development/update' : '/api/pipeline/ideas/update';
          await axios.post(endpoint, form);
        } else {
          await axios.post('/api/pipeline/ideas/create', form);
          await this.getAllHandles();
        }
        await this.loadIdeas();
        await this.loadDevelopments();
        this.closeDialog();
      } catch (e) {
        console.error('Fehler beim Speichern!', e);
        this.dialogError = e.response?.data?.message || 'Fehler beim Speichern';
      } finally {
        this.saving = false;
      }
    },

    moveToDevDialog(idea) {
      this.selectedIdea = idea;
      this.moveDialog = true;
    },

    async confirmMoveToDev() {
      this.moving = true;
      try {
        await axios.post('/api/pipeline/ideas/move-to-development', { id: this.selectedIdea.id });
        await this.loadIdeas();
        await this.loadDevelopments();
        this.moveDialog = false;
        this.selectedIdea = null;
        this.activeTab = 'development';
      } catch (e) {
        console.error('Fehler beim Verschieben!', e);
        alert('Fehler beim Verschieben in die Entwicklung');
      } finally {
        this.moving = false;
      }
    },

    openSectionImageDialog(dev) {
      this.selectedDev = dev;
      this.sectionImageFile = null;
      this.sectionImagePreview = null;
      this.sectionImageMode = 'upload';
      this.selectedExistingImageIndex = null;
      this.sectionImageDialog = true;
    },

    closeSectionImageDialog() {
      this.sectionImageDialog = false;
      this.selectedDev = null;
      this.sectionImageFile = null;
      this.sectionImagePreview = null;
      this.sectionImageMode = 'upload';
      this.selectedExistingImageIndex = null;
    },

    onSectionImageSelected(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      this.sectionImageFile = file;
      const reader = new FileReader();
      reader.onload = () => { this.sectionImagePreview = reader.result; };
      reader.readAsDataURL(file);
    },

    async uploadSectionImage() {
      this.uploadingSectionImage = true;
      try {
        const form = new FormData();
        form.append('id', this.selectedDev.id);
        form.append('sectionImage', this.sectionImageFile);
        await axios.post('/api/pipeline/development/upload-section-image', form);
        await this.loadDevelopments();
        this.closeSectionImageDialog();
      } catch (e) {
        console.error('Fehler beim Hochladen!', e);
        alert('Fehler beim Hochladen des Section Images');
      } finally {
        this.uploadingSectionImage = false;
      }
    },

    async useExistingImageAsSectionImage() {
      if (this.selectedExistingImageIndex === null || !this.selectedDev) return;
      this.uploadingSectionImage = true;
      try {
        const imageUrl = `/api/pipeline/development/image/${this.selectedDev.id}/${this.selectedExistingImageIndex}`;
        const response = await axios.get(imageUrl, { responseType: 'blob' });
        const form = new FormData();
        form.append('id', this.selectedDev.id);
        form.append('sectionImage', response.data, 'section-image.png');
        await axios.post('/api/pipeline/development/upload-section-image', form);
        await this.loadDevelopments();
        this.closeSectionImageDialog();
      } catch (e) {
        console.error('Fehler beim Übernehmen!', e);
        alert('Fehler beim Übernehmen des Bildes');
      } finally {
        this.uploadingSectionImage = false;
      }
    },

    openCreateSectionDialog(dev) {
      if (!dev.sectionImage) { alert('Bitte laden Sie zuerst ein Section Image hoch!'); return; }
      this.selectedDevForSection = dev;
      this.createSectionError = '';
      this.createSectionDialog = true;
    },

    closeCreateSectionDialog() {
      this.createSectionDialog = false;
      this.selectedDevForSection = null;
      this.createSectionError = '';
    },

    async confirmCreateSection() {
      this.creatingSection = true;
      this.createSectionError = '';
      try {
        const { data } = await axios.post('/api/pipeline/development/create-section', { id: this.selectedDevForSection.id });
        if (data.success && data.sectionId) {
          await this.loadDevelopments();
          this.closeCreateSectionDialog();
          this.$router.push({ name: 'SectionDetails', params: { id: data.sectionId } });
        }
      } catch (e) {
        console.error('Fehler beim Erstellen der Section!', e);
        this.createSectionError = e.response?.data?.message || 'Fehler beim Erstellen der Section';
      } finally {
        this.creatingSection = false;
      }
    },

    async deleteItem(id, type) {
      const typeName = type === 'idea' ? 'Idee' : 'Entwicklung';
      if (!confirm(`Möchten Sie diese ${typeName} wirklich löschen?`)) return;
      try {
        const endpoint = type === 'idea' ? '/api/pipeline/ideas/delete' : '/api/pipeline/development/delete';
        await axios.post(endpoint, { id });
        await this.loadIdeas();
        await this.loadDevelopments();
      } catch (e) {
        console.error('Fehler beim Löschen!', e);
        alert(`Fehler beim Löschen der ${typeName}`);
      }
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 26px; font-weight: 800; color: #0a1628; letter-spacing: -0.3px; margin-bottom: 6px; }
.page-subtitle { font-size: 13px; color: #888; }

/* Buttons */
.primary-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 18px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700; cursor: pointer;
  transition: background 0.15s;
}
.primary-btn:hover:not(:disabled) { background: #1d3457; }

/* Tab Bar */
.tab-bar { display: flex; align-items: center; gap: 6px; }
.tab {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 8px 14px; background: white; border: 1px solid #eceef2;
  border-radius: 10px; font-size: 13px; font-weight: 600; color: #666;
  cursor: pointer; transition: all 0.15s;
}
.tab:hover:not(.tab-active) { border-color: #c5cbd3; color: #0a1628; }
.tab-active { background: #0a1628; color: white; border-color: #0a1628; }
.tab-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 20px; padding: 0 6px; border-radius: 10px;
  background: rgba(10,22,40,0.08); color: #0a1628; font-size: 11px; font-weight: 700;
}
.tab-active .tab-count { background: rgba(255,255,255,0.2); color: white; }

.view-toggle {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px; background: white; border: 1px solid #eceef2; border-radius: 10px; height: 40px;
}
.view-btn {
  width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 7px; cursor: pointer; color: #888;
  transition: all 0.15s;
}
.view-btn:hover:not(.active) { background: #f8f9fb; color: #0a1628; }
.view-btn.active { background: #0a1628; color: white; }
.view-btn.active :deep(.v-icon) { color: white !important; }

/* Grid */
.grid-items { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 16px; }
.item-card { background: white; border: 1px solid #eceef2; border-radius: 14px; overflow: hidden; transition: all 0.15s; }
.item-card:hover { border-color: #0a1628; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(10,22,40,0.06); }

.item-thumb {
  position: relative; background: #f3f4f6; padding: 12px; cursor: zoom-in;
  border-radius: 14px 14px 0 0;
}
.item-thumb > :deep(.v-img),
.item-thumb > .thumb-fallback {
  border-radius: 10px;
  overflow: hidden;
}
.thumb-fallback {
  width: 100%; height: 100%; aspect-ratio: 1; display: flex;
  align-items: center; justify-content: center; background: #ffffff;
}
.thumb-count {
  position: absolute; bottom: 20px; right: 20px;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 20px;
  background: rgba(10,22,40,0.75); color: white;
  font-size: 10px; font-weight: 700; backdrop-filter: blur(4px);
}
.thumb-count.thumb-success { background: rgba(46,125,50,0.9); }

.handle-chip {
  display: inline-block; max-width: 100%;
  padding: 3px 9px; border-radius: 6px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.3px;
  font-family: 'SFMono-Regular', Menlo, monospace;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  vertical-align: middle;
}
.chip-ideas { background: rgba(91,91,214,0.12); color: #5B5BD6; }
.chip-dev { background: rgba(255,152,0,0.12); color: #E65100; }

.item-name {
  font-size: 14px; font-weight: 700; color: #0a1628;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.item-sub {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; color: #888; margin-top: 3px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

.item-meta {
  display: flex; align-items: center; justify-content: space-between;
  padding-top: 10px; border-top: 1px solid #f5f5f5;
}
.meta-assigned {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; color: #555; font-weight: 500; min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.meta-assigned-empty { color: #bbb; font-style: italic; }
.meta-date { font-size: 11px; color: #999; white-space: nowrap; }

.item-actions {
  display: flex; gap: 4px; margin-top: 12px;
  padding-top: 10px; border-top: 1px solid #f5f5f5;
}
.action-btn {
  flex: 1; height: 32px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: 1px solid #eceef2; border-radius: 8px; cursor: pointer;
  color: #666; transition: all 0.15s;
}
.action-btn:hover:not(:disabled) { background: #f8f9fb; color: #0a1628; border-color: #0a1628; }
.action-primary:hover:not(:disabled) { border-color: #E65100; color: #E65100; background: rgba(255,152,0,0.06); }
.action-success:hover:not(:disabled) { border-color: #2E7D32; color: #2E7D32; background: rgba(46,125,50,0.06); }
.action-danger:hover:not(:disabled) { border-color: #D32F2F; color: #D32F2F; background: rgba(211,47,47,0.06); }
.action-disabled, .action-btn:disabled { opacity: 0.35; cursor: not-allowed; }

/* List */
.list-wrap { background: white; border: 1px solid #eceef2; border-radius: 14px; overflow: hidden; }
.list-header {
  display: grid; align-items: center; gap: 16px; padding: 12px 20px;
  background: #fafbfc; border-bottom: 1px solid #eceef2;
  font-size: 11px; font-weight: 700; color: #999;
  text-transform: uppercase; letter-spacing: 0.3px;
}
.list-row {
  display: grid; align-items: center; gap: 16px; padding: 12px 20px;
  border-bottom: 1px solid #f5f5f5; transition: background 0.15s;
}
.list-row:last-child { border-bottom: none; }
.list-row:hover { background: #fafbfc; }
.list-cols-ideas { grid-template-columns: 56px minmax(0, 160px) minmax(0, 1.6fr) minmax(0, 1.2fr) minmax(0, 1.2fr) 60px 130px 140px; }
.list-cols-dev   { grid-template-columns: 56px minmax(0, 160px) minmax(0, 1.6fr) minmax(0, 1.2fr) 120px 130px 170px; }

.list-thumb {
  width: 40px; height: 40px; border-radius: 8px; overflow: hidden;
  background: #f8f9fb; border: 1px solid #eceef2; cursor: zoom-in;
}
.list-name { font-size: 13px; font-weight: 600; color: #0a1628; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.list-muted { font-size: 12px; color: #999; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.list-actions { display: flex; gap: 4px; justify-content: flex-end; }

/* Pills */
.pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 700;
}
.pill-success { background: rgba(46,125,50,0.1); color: #2E7D32; }
.pill-warning { background: rgba(255,152,0,0.1); color: #E65100; }
.pill-neutral { background: #f0f2f5; color: #666; }

/* Empty */
.empty-state { background: white; border: 1px solid #eceef2; border-radius: 16px; padding: 64px 32px; text-align: center; }
.empty-title { font-size: 16px; font-weight: 700; color: #0a1628; margin-bottom: 6px; }
.empty-desc { font-size: 13px; color: #888; }
.empty-inline { font-size: 13px; color: #999; text-align: center; padding: 32px 0; }

/* Skeleton */
.loading-state { display: flex; flex-direction: column; gap: 8px; }
.skel-row {
  height: 68px; border-radius: 12px;
  background: linear-gradient(90deg, #eceef2 25%, #f5f6f8 50%, #eceef2 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite;
}
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

/* Dialog (floowseo pattern) */
.dialog-backdrop { position: fixed; inset: 0; background: rgba(10,22,40,0.5); z-index: 2000; animation: fadeBg 0.2s ease; }
@keyframes fadeBg { from { opacity: 0; } to { opacity: 1; } }
.modern-dialog {
  background: white !important;
  box-shadow: 0 24px 64px rgba(10,22,40,0.18) !important;
  z-index: 2001 !important; position: relative;
  animation: slideUp 0.3s cubic-bezier(0.16,1,0.3,1);
  overflow: visible !important;
}
@keyframes slideUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

.close-btn {
  position: absolute; top: 16px; right: 16px; z-index: 10;
  width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 8px; cursor: pointer; color: #888;
  transition: all 0.2s;
}
.close-btn:hover { background: #f0f2f5; color: #0a1628; transform: rotate(90deg); }
.close-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.dh { display: flex; align-items: center; gap: 14px; padding: 28px 28px 0; }
.dh-icon {
  width: 44px; height: 44px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(10,22,40,0.06); flex-shrink: 0;
}
.dh-title { font-size: 18px; font-weight: 700; color: #0a1628; }
.dh-sub { font-size: 12px; color: #999; margin-top: 2px; }

.db { padding: 24px 28px; max-height: 70vh; overflow-y: auto; }
.dff {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 10px; padding: 4px 28px 24px;
}

.fg { display: flex; flex-direction: column; }
.fl {
  font-size: 11px; font-weight: 600; color: #999;
  margin-bottom: 6px; letter-spacing: 0.3px;
}
.fl-hint { color: #bbb; font-weight: 400; text-transform: none; letter-spacing: 0; margin-left: 4px; }
.iw {
  display: flex; align-items: center; gap: 10px;
  height: 42px; padding: 0 14px;
  background: #f8f9fb; border: 1px solid #eceef2; border-radius: 10px;
  transition: all 0.15s;
}
.iw.iw-f { border-color: #0a1628; background: white; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.iw.iw-readonly { opacity: 0.7; }
.ii {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 14px; color: #0a1628; font-weight: 500; min-width: 0; font-family: inherit;
}
.ii::placeholder { color: #bbb; font-weight: 400; }
.ii:disabled, .ii[readonly] { opacity: 0.7; cursor: default; }
.ii-select { cursor: pointer; appearance: none; padding-right: 20px; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%23888' d='M6 8L0 0h12z'/></svg>"); background-repeat: no-repeat; background-position: right center; }

/* Employee dropdown */
.emp-wrap { position: relative; }

.emp-trigger {
  display: flex; align-items: center; gap: 10px; width: 100%;
  height: 42px; padding: 0 14px;
  background: #f8f9fb; border: 1px solid #eceef2; border-radius: 10px;
  cursor: pointer; transition: all 0.15s; font-family: inherit;
  text-align: left;
}
.emp-trigger:hover:not(:disabled) { border-color: #c5cbd3; }
.emp-trigger-open {
  border-color: #0a1628 !important; background: white !important;
  box-shadow: 0 0 0 3px rgba(10,22,40,0.04);
}
.emp-trigger:disabled { opacity: 0.6; cursor: not-allowed; }

.emp-avatar {
  width: 24px; height: 24px; min-width: 24px; border-radius: 50%;
  background: #e8eaed; display: flex; align-items: center; justify-content: center;
  overflow: hidden;
}
.emp-avatar-empty { background: #f0f2f5; border: 1px dashed #c5cbd3; }

.emp-name {
  flex: 1; font-size: 14px; color: #0a1628; font-weight: 500;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.emp-placeholder { flex: 1; font-size: 14px; color: #bbb; font-weight: 400; }
.emp-position { font-size: 11px; color: #888; font-weight: 400; margin-top: 1px; }

.emp-chev { transition: transform 0.2s; flex-shrink: 0; }
.emp-chev-open { transform: rotate(180deg); }

.emp-menu {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  box-shadow: 0 12px 32px rgba(10,22,40,0.12);
  z-index: 50; max-height: 260px; overflow-y: auto;
  padding: 6px; animation: empFadeIn 0.12s ease;
}

/* Reserviert im Dialog Platz, damit das Menü nicht abgeschnitten wird */
.emp-spacer { height: 268px; animation: empFadeIn 0.15s ease; }
@keyframes empFadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}

.emp-option {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 8px 10px; background: transparent; border: none;
  border-radius: 7px; cursor: pointer; transition: background 0.1s;
  text-align: left; font-family: inherit;
}
.emp-option:hover { background: #f8f9fb; }
.emp-option-active { background: rgba(46,125,50,0.06); }
.emp-option-active .emp-name { color: #2E7D32; font-weight: 600; }
.emp-option-body { flex: 1; min-width: 0; }

.emp-divider { height: 1px; background: #eceef2; margin: 4px 0; }

.bo {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 18px; border: 1px solid #eceef2; border-radius: 10px;
  background: white; font-size: 13px; font-weight: 600; color: #555;
  cursor: pointer; transition: border-color 0.15s;
}
.bo:hover:not(:disabled) { border-color: #0a1628; }
.bo:disabled { opacity: 0.5; cursor: not-allowed; }

.bp {
  display: inline-flex; align-items: center; gap: 6px; min-width: 130px;
  padding: 10px 20px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: background 0.15s; justify-content: center;
}
.bp:hover:not(:disabled) { background: #1d3457; }
.bp:disabled { opacity: 0.5; cursor: not-allowed; }
.bp-warning { background: #E65100; }
.bp-warning:hover:not(:disabled) { background: #BF360C; }
.bp-success { background: #2E7D32; }
.bp-success:hover:not(:disabled) { background: #1B5E20; }

.bs {
  display: inline-flex; align-items: center; gap: 8px; width: 100%; justify-content: center;
  padding: 10px 14px; border: 1px dashed #c5cbd3; border-radius: 10px; background: #fafbfc;
  font-size: 13px; font-weight: 600; color: #555; cursor: pointer; transition: all 0.15s;
}
.bs:hover:not(:disabled) { border-color: #0a1628; color: #0a1628; background: white; }
.bs:disabled { opacity: 0.5; cursor: not-allowed; }

.alert-error {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: rgba(211,47,47,0.08); color: #D32F2F;
  border-radius: 10px; font-size: 13px; font-weight: 500;
}

.info-banner {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px; background: rgba(91,91,214,0.08); color: #3A3AC0;
  border-radius: 10px; font-size: 13px; font-weight: 500;
}

.summary-card {
  background: #fafbfc; border: 1px solid #eceef2; border-radius: 12px; padding: 14px 16px;
}
.summary-name { font-size: 14px; font-weight: 700; color: #0a1628; margin-top: 4px; }
.summary-sub { font-size: 12px; color: #888; margin-top: 2px; }

/* Viewer */
.viewer-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 12px; }
.viewer-item { border-radius: 10px; overflow: hidden; border: 1px solid #eceef2; }

/* Preview */
.images-preview-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)); gap: 8px; }
.preview-thumb { position: relative; border-radius: 10px; overflow: hidden; border: 1px solid #eceef2; }
.preview-remove {
  position: absolute; top: 6px; right: 6px;
  width: 22px; height: 22px; border-radius: 50%; border: none;
  background: rgba(10,22,40,0.75); color: white; cursor: pointer;
  display: flex; align-items: center; justify-content: center; transition: background 0.15s;
}
.preview-remove:hover { background: #D32F2F; }

/* Mode toggle */
.mode-toggle { display: flex; gap: 4px; padding: 4px; background: #f8f9fb; border: 1px solid #eceef2; border-radius: 10px; }
.mode-btn {
  flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 8px 12px; background: transparent; border: none; border-radius: 7px;
  font-size: 12px; font-weight: 600; color: #888; cursor: pointer; transition: all 0.15s;
}
.mode-btn:hover:not(.mode-active):not(:disabled) { background: white; color: #0a1628; }
.mode-active { background: white; color: #0a1628; box-shadow: 0 1px 3px rgba(10,22,40,0.08); }
.mode-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* Upload box */
.upload-box {
  position: relative; background: #f8f9fb; border: 1px dashed #c5cbd3; border-radius: 12px;
  overflow: hidden; cursor: pointer; transition: all 0.15s; min-height: 220px;
}
.upload-box:hover { border-color: #0a1628; background: white; }
.upload-placeholder {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 220px; padding: 24px;
}
.upload-title { font-size: 14px; font-weight: 700; color: #0a1628; margin-bottom: 2px; }
.upload-sub { font-size: 12px; color: #888; }

/* Existing images grid */
.existing-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)); gap: 10px; }
.existing-item {
  position: relative; border-radius: 10px; overflow: hidden;
  border: 2px solid #eceef2; cursor: pointer; transition: all 0.15s;
}
.existing-item:hover { border-color: #0a1628; }
.existing-selected { border-color: #2E7D32 !important; }
.existing-check {
  position: absolute; top: 6px; right: 6px;
  background: white; border-radius: 50%;
}

@media (max-width: 1100px) {
  .list-cols-ideas { grid-template-columns: 56px minmax(0, 1fr) 120px 140px; }
  .list-cols-dev   { grid-template-columns: 56px minmax(0, 1fr) 120px 160px; }
  .list-header > *:nth-child(4), .list-header > *:nth-child(5), .list-header > *:nth-child(6),
  .list-row > *:nth-child(4), .list-row > *:nth-child(5), .list-row > *:nth-child(6) { display: none; }
  .list-header > *:last-child, .list-row > *:last-child { display: flex !important; }
  .list-header > *:nth-child(3), .list-row > *:nth-child(3) { display: block !important; }
}

@media (max-width: 600px) {
  .tab-bar { flex-wrap: wrap; }
  .view-toggle { order: 10; }
}
</style>
